/**
 * SupportTicketDetailPage — admin ticket workspace at /support/:id.
 *
 * Layout:
 *   - Header: subject, user, status/priority chips, action toolbar
 *   - Thread: full message list (including internal admin-only notes)
 *   - Reply composer with internal-note toggle
 *   - Right rail: status / priority / assignment controls + activity log
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronLeft, ExternalLink, Loader2 } from 'lucide-react';
import { cn } from '@/src/infrastructure/lib/utils';
import {
  assignSupportTicket,
  getSupportTicket,
  replyToSupportTicket,
  updateSupportTicketPriority,
  updateSupportTicketStatus,
  type AdminSupportMessage,
  type AdminSupportTicket,
  type AdminSupportTicketPriority,
  type AdminSupportTicketStatus,
} from '@/src/infrastructure/lib/api';
import {
  PRIORITY_TONE,
  STATUS_LABEL,
  STATUS_TONE,
  SUPPORT_PRIORITIES,
  SUPPORT_STATUSES,
} from './constants';
import { useAuth } from '@/src/modules/auth/useAuth';

function formatTime(iso: string) {
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function MessageRow({ msg }: { msg: AdminSupportMessage }) {
  const isAdmin = msg.senderType === 'ADMIN';
  const isSystem = msg.senderType === 'SYSTEM';
  return (
    <motion.li
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        'rounded-2xl p-3 border',
        msg.internalNote
          ? 'border-amber-500/40 bg-amber-500/5'
          : isAdmin
            ? 'border-primary/30 bg-primary/5'
            : isSystem
              ? 'border-violet-500/30 bg-violet-500/5 italic'
              : 'border-border-subtle bg-surface-low/40',
      )}
    >
      <header className="flex items-center justify-between text-[10px] text-on-surface-variant">
        <div className="flex items-center gap-2 font-semibold uppercase tracking-wider">
          {msg.senderType}
          {msg.sender?.name && (
            <span className="text-on-surface-variant normal-case">
              · {msg.sender.name}
            </span>
          )}
          {msg.internalNote && (
            <span className="rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-700 px-2 py-0.5">
              Internal
            </span>
          )}
        </div>
        <span>{formatTime(msg.createdAt)}</span>
      </header>
      <p className="mt-2 text-sm whitespace-pre-wrap text-on-surface">{msg.message}</p>
      {msg.attachments.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-1">
          {msg.attachments.map((a) => (
            <li key={a.id}>
              <a
                href={a.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded-full border border-border-subtle px-2 py-0.5 text-[10px] text-on-surface-variant hover:text-primary"
              >
                <ExternalLink className="w-3 h-3" />
                Attachment
              </a>
            </li>
          ))}
        </ul>
      )}
    </motion.li>
  );
}

export default function SupportTicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { profile } = useAuth();
  const [ticket, setTicket] = useState<AdminSupportTicket | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reply, setReply] = useState('');
  const [internalNote, setInternalNote] = useState(false);
  const [acting, setActing] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    getSupportTicket(id)
      .then((t) => !cancelled && setTicket(t))
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function refresh() {
    if (!id) return;
    const t = await getSupportTicket(id);
    setTicket(t);
  }

  async function sendReply() {
    if (!id || !reply.trim() || acting) return;
    setActing(true);
    try {
      const updated = await replyToSupportTicket(id, {
        message: reply.trim(),
        internalNote,
      });
      setTicket(updated);
      setReply('');
      setInternalNote(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reply');
    } finally {
      setActing(false);
    }
  }

  async function changeStatus(s: AdminSupportTicketStatus) {
    if (!id) return;
    setActing(true);
    try {
      const updated = await updateSupportTicketStatus(id, s);
      setTicket(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Status change failed');
    } finally {
      setActing(false);
    }
  }

  async function changePriority(p: AdminSupportTicketPriority) {
    if (!id) return;
    setActing(true);
    try {
      const updated = await updateSupportTicketPriority(id, p);
      setTicket(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Priority change failed');
    } finally {
      setActing(false);
    }
  }

  async function toggleAssign() {
    if (!id || !profile?.id) return;
    setActing(true);
    try {
      const newAssignee =
        ticket?.assignedAdminId === profile.id ? null : profile.id;
      const updated = await assignSupportTicket(id, newAssignee);
      setTicket(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Assignment failed');
    } finally {
      setActing(false);
    }
  }

  if (loading) {
    return (
      <div className="p-10 text-center text-on-surface-variant">
        <Loader2 className="w-5 h-5 mx-auto animate-spin" />
        <p className="mt-2 text-xs">Loading ticket…</p>
      </div>
    );
  }
  if (!ticket) {
    return (
      <div className="p-10 text-center">
        <p className="text-sm text-on-surface-variant">{error || 'Ticket not found.'}</p>
        <Link to="/support" className="mt-4 inline-flex text-xs font-semibold text-primary">
          ← Back to tickets
        </Link>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6">
      <Link
        to="/support"
        className="inline-flex items-center gap-1 text-xs font-semibold text-on-surface-variant hover:text-on-surface"
      >
        <ChevronLeft className="w-4 h-4" /> All tickets
      </Link>

      <header className="rounded-3xl border border-border-subtle bg-surface-low/40 p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-on-surface-variant">
              {ticket.category} · #{ticket.id.slice(0, 8)}
            </p>
            <h1 className="mt-1 text-xl md:text-2xl font-display font-bold text-on-surface">
              {ticket.subject}
            </h1>
            <p className="mt-2 text-xs text-on-surface-variant">
              From {ticket.user?.name ?? ticket.user?.email ?? '—'} ·{' '}
              {formatTime(ticket.createdAt)}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span
              className={cn(
                'rounded-full border px-2 py-0.5 text-[10px] font-semibold',
                STATUS_TONE[ticket.status],
              )}
            >
              {STATUS_LABEL[ticket.status]}
            </span>
            <span
              className={cn(
                'text-[10px] font-semibold uppercase',
                PRIORITY_TONE[ticket.priority],
              )}
            >
              {ticket.priority}
            </span>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_18rem] gap-6">
        {/* Thread + composer */}
        <div className="space-y-4">
          <ul className="space-y-3">
            <AnimatePresence initial={false}>
              {ticket.messages.map((m) => (
                <MessageRow key={m.id} msg={m} />
              ))}
            </AnimatePresence>
          </ul>

          <div className="rounded-2xl border border-border-subtle bg-surface-low/40 p-4 space-y-3">
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={5}
              placeholder={internalNote ? 'Internal note — invisible to user' : 'Reply to user…'}
              className={cn(
                'w-full rounded-xl border bg-surface px-3 py-2 text-sm focus:border-primary focus:outline-none resize-y',
                internalNote ? 'border-amber-500/40 bg-amber-500/5' : 'border-border-subtle',
              )}
            />
            <div className="flex items-center justify-between">
              <label className="inline-flex items-center gap-2 text-xs text-on-surface-variant">
                <input
                  type="checkbox"
                  checked={internalNote}
                  onChange={(e) => setInternalNote(e.target.checked)}
                  className="accent-amber-600"
                />
                Internal note (admin-only)
              </label>
              <button
                type="button"
                disabled={!reply.trim() || acting}
                onClick={sendReply}
                className={cn(
                  'inline-flex items-center gap-2 rounded-full px-5 py-2 text-xs font-semibold shadow-sm transition-colors',
                  reply.trim() && !acting
                    ? 'bg-primary text-white hover:opacity-90'
                    : 'bg-surface-low text-on-surface-variant cursor-not-allowed',
                )}
              >
                {acting ? 'Sending…' : internalNote ? 'Add note' : 'Send reply'}
              </button>
            </div>
          </div>
        </div>

        {/* Right rail */}
        <aside className="space-y-4">
          <div className="rounded-2xl border border-border-subtle bg-surface-low/40 p-4 space-y-3">
            <h2 className="text-xs uppercase tracking-wider font-semibold text-on-surface-variant">
              Status
            </h2>
            <div className="flex flex-wrap gap-1">
              {SUPPORT_STATUSES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => changeStatus(s)}
                  disabled={acting || s === ticket.status}
                  className={cn(
                    'rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-colors',
                    s === ticket.status
                      ? cn(STATUS_TONE[s], 'opacity-70 cursor-not-allowed')
                      : 'border-border-subtle text-on-surface-variant hover:border-primary/40 hover:text-on-surface',
                  )}
                >
                  {STATUS_LABEL[s]}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-border-subtle bg-surface-low/40 p-4 space-y-3">
            <h2 className="text-xs uppercase tracking-wider font-semibold text-on-surface-variant">
              Priority
            </h2>
            <div className="flex flex-wrap gap-1">
              {SUPPORT_PRIORITIES.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => changePriority(p)}
                  disabled={acting || p === ticket.priority}
                  className={cn(
                    'rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-colors',
                    p === ticket.priority
                      ? cn('border-primary bg-primary/10 text-primary', 'cursor-not-allowed')
                      : 'border-border-subtle text-on-surface-variant hover:border-primary/40 hover:text-on-surface',
                  )}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-border-subtle bg-surface-low/40 p-4 space-y-3">
            <h2 className="text-xs uppercase tracking-wider font-semibold text-on-surface-variant">
              Assignment
            </h2>
            <p className="text-xs text-on-surface">
              {ticket.assignedAdmin?.name ?? 'Unassigned'}
            </p>
            <button
              type="button"
              onClick={toggleAssign}
              disabled={acting}
              className="w-full rounded-full border border-border-subtle px-3 py-1.5 text-[11px] font-semibold text-on-surface-variant hover:border-primary/40 hover:text-on-surface"
            >
              {ticket.assignedAdminId === profile?.id ? 'Unassign myself' : 'Assign to me'}
            </button>
          </div>

          {ticket.activity && ticket.activity.length > 0 && (
            <div className="rounded-2xl border border-border-subtle bg-surface-low/40 p-4 space-y-2">
              <h2 className="text-xs uppercase tracking-wider font-semibold text-on-surface-variant">
                Activity
              </h2>
              <ul className="space-y-1 text-[11px] text-on-surface-variant max-h-72 overflow-auto">
                {ticket.activity.map((a) => (
                  <li key={a.id} className="flex justify-between gap-2">
                    <span className="font-semibold text-on-surface">{a.action}</span>
                    <span>{formatTime(a.createdAt)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
